import {Button, Grid, Typography} from '@mui/material';
import React from 'react';
import {useMutation} from '@apollo/client';
import {Box} from '@mui/system';
import {useSnackbar} from 'notistack';
import moment from 'moment';
import withAutoFormNew from '../../components/AutoForm/AutoFormNew';
import AutoField from '../../components/AutoForm/AutoFieldNew';
import {
  reportPackISAManagerField,
  reportPackSortCodeField,
  reportPackAccountField,
  ReportPackGenericFormType,
  reportPackFormType,
  isaManagerPostcodeField,
  isaManagerAddress1Field,
  isaManagerAddress2Field,
  isaTransferTypeField,
  isaManagerCityField,
  ReportPackGenericFormInnerType,
} from './ReportPackFields';
import {upsertJISATransferForm} from './reportPackQueries';
import {useAutoForm} from '../../components/AutoForm/useAutoForm';
import {
  UpsertJisaTransferForm,
  UpsertJisaTransferFormVariables,
} from '../../schema-types/UpsertJisaTransferForm';
import {ReportPackFormFragment} from '../../schema-types/ReportPackFormFragment';
import {getReportPackMissingFieldError} from './getReportPackMissingFieldError';
import {DependantForm} from './DependantForm';

export const ReportPackJISATransfer: React.FC<ReportPackGenericFormType> = ({
  formData,
  onClose,
  disabled,
}) => {
  const {enqueueSnackbar, closeSnackbar} = useSnackbar();
  const [onUpsertJISAForm, {loading: onUpsertJISAFormLoading}] = useMutation<
    UpsertJisaTransferForm,
    UpsertJisaTransferFormVariables
  >(upsertJISATransferForm);
  return (
    <ReportPackJISATransferFormWrapped
      formFields={[]}
      disabled={disabled}
      submit={async (values) => {
        if (disabled) {
          return;
        }
        closeSnackbar();
        enqueueSnackbar(`Saving ${formData.formTypeLabel}...`, {
          variant: 'info',
        });
        await onUpsertJISAForm({
          variables: {
            contactCuroId: values.contactCuroId,
            isaManagerAddress1: values.isaManagerAddress1,
            isaManagerAddress2: values.isaManagerAddress2,
            isaManagerName: values.isaManagerName,
            isaManagerPostcode: values.isaManagerPostcode,
            isaManagerSortCode: values.isaManagerSortCode,
            isaManagerPlanRef: values.isaManagerPlanRef,
            isaManagerCity: values.isaManagerCity,
            formTransferType: values.formTransferType,
            childDob: moment(values.childDob),
            childForenames: values.childForenames,
            childNiNumber: values.childNiNumber,
            childPostcode: values.childPostcode,
            childSurname: values.childSurname,
            childAddress: values.childAddress,
            childTitle: values.childTitle,
            reportPackId: String(formData.reportPackId),
            formId: formData.id,
          },
        })
          .then(({data}) => {
            if (data?.upsertJisaTransferForm?.ok) {
              closeSnackbar();
              enqueueSnackbar(`Successfully saved ${formData.formTypeLabel}`, {
                variant: 'success',
              });
              onClose();
            } else {
              throw Error(data?.upsertJisaTransferForm?.errors?.join(','));
            }
          })
          .catch((err) => {
            closeSnackbar();
            enqueueSnackbar(`Could not save ${formData.formTypeLabel} ${err}`, {
              variant: 'error',
            });
          });
      }}
      initialValueOverrides={{
        ...formData,
        reportPackId: String(formData.reportPackId),
        contactCuroId: String(formData.contactCuroId),
      }}
    />
  );
};

const ReportPackJISATransferForm: React.FC<ReportPackGenericFormInnerType> = ({
  disabled,
}) => {
  const {submitForm, isSubmitting, values} = useAutoForm<ReportPackFormFragment>();

  return (
    <Box maxWidth={500}>
      <DependantForm disabled={disabled} />
      <Typography>Current provider details:</Typography>
      <Grid container p={2} mb={2} spacing={2} direction="column">
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(values, reportPackISAManagerField.id)}
            field={reportPackISAManagerField}
          />
        </Grid>
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(values, reportPackAccountField.id)}
            field={reportPackAccountField}
          />
        </Grid>
        <Grid item>
          <AutoField
            readOnly={disabled}
            field={{...reportPackSortCodeField, label: 'Sort code (if cash JISA only)'}}
          />
        </Grid>
      </Grid>
      <Typography>Current provider address:</Typography>
      <Grid container p={2} mb={2} spacing={2} direction="column">
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(values, isaManagerAddress1Field.id)}
            field={isaManagerAddress1Field}
          />
        </Grid>
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(values, isaManagerAddress2Field.id)}
            field={isaManagerAddress2Field}
          />
        </Grid>
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(values, isaManagerCityField.id)}
            field={isaManagerCityField}
          />
        </Grid>
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(values, isaManagerPostcodeField.id)}
            field={isaManagerPostcodeField}
          />
        </Grid>
      </Grid>
      <Typography>Transfer type:</Typography>
      <Grid container p={2} mb={2} spacing={2} direction="column">
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(values, isaTransferTypeField.id)}
            field={isaTransferTypeField}
          />
        </Grid>
      </Grid>
      <Box position="absolute" bottom={20} right={20}>
        {!disabled && (
          <Button
            variant="contained"
            color="secondary"
            disabled={isSubmitting}
            onClick={() => submitForm()}
          >
            Save form
          </Button>
        )}
      </Box>
    </Box>
  );
};

const ReportPackJISATransferFormWrapped = withAutoFormNew<
  reportPackFormType,
  ReportPackGenericFormInnerType
>(ReportPackJISATransferForm, {}, {debugPosition: 'bottom'});
